<template>
  <section id="about" class="about-section">
    <div class="container">
      <div class="about-visual">
        <div class="about-orb">
          <div class="orb-ring ring-1"></div>
          <div class="orb-ring ring-2"></div>
          <div class="orb-center"></div>
        </div>

        <div class="exp-badge">
          <span class="exp-num">+5</span>
          <span class="exp-label">سنوات خبرة</span>
        </div>

        <div class="team-grid">
          <div v-for="n in 6" :key="n" class="team-avatar">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
          </div>
        </div>

        <div class="about-quote">
          <span class="quote-mark">&quot;</span>
          نؤمن ان كل مشروع هو قصة نجاح نكتبها معا
        </div>
      </div>

      <div class="about-content">
        <p class="pre-label">من نحن</p>
        <h2 class="about-title">
          شركاؤك في
          <span class="highlight">النجاح الرقمي</span>
        </h2>
        <div class="gold-divider"></div>

        <p class="about-text">
          نحن فريق من المطورين والمصممين المحترفين نؤمن بأن التكنولوجيا هي جسر
          النجاح لكل عمل تجاري. نجمع بين الابداع التقني والجماليات البصرية
          لتقديم حلول رقمية تتجاوز توقعاتك.
        </p>

        <p class="about-text">
          منذ انطلاقنا، أنجزنا أكثر من 200 مشروع لعملاء من مختلف القطاعات
          والمجالات، وبنينا سمعة راسخة قائمة على الجودة والالتزام والابتكار.
        </p>

        <div class="values-grid">
          <div v-for="v in values" :key="v.label" class="value-item">
            <div class="value-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--gold)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path :d="v.iconPath" />
              </svg>
            </div>
            <div class="value-text">
              <strong>{{ v.label }}</strong>
              <span>{{ v.desc }}</span>
            </div>
          </div>
        </div>

        <a href="#contact" class="btn-cta">ابدأ مشروعك معنا اليوم</a>
      </div>
    </div>
  </section>
</template>

<script setup>
const values = [
  {
    label: "سرعة التسليم",
    desc: "نلتزم بكل موعد",
    iconPath: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  },
  {
    label: "دقة التنفيذ",
    desc: "جودة في كل تفصيل",
    iconPath: "M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3",
  },
  {
    label: "ابتكار مستمر",
    desc: "نواكب احدث التقنيات",
    iconPath:
      "M12 2a7 7 0 0 1 7 7c0 3-1.86 5.58-4.57 6.68L14 17H10l.57-1.32A7 7 0 0 1 5 9a7 7 0 0 1 7-7zM9 21h6",
  },
  {
    label: "شراكة حقيقية",
    desc: "نجاحك هو نجاحنا",
    iconPath:
      "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  },
];
</script>

<style scoped>
/* التنسيقات بقيت كما هي لضمان عدم تأثر التصميم */
.about-section {
  padding: 7rem 6vw;
  background: var(--bg-navy);
  position: relative;
}

.about-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  gap: 5rem;
  align-items: center;
}

.about-visual {
  flex: 1;
  position: relative;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.about-orb {
  width: 300px;
  height: 300px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.orb-ring {
  position: absolute;
  border-radius: 50%;
  border: 1px solid var(--border);
}

.ring-1 {
  width: 300px;
  height: 300px;
  border-top-color: var(--gold);
  animation: spin-slow 20s linear infinite;
}

.ring-2 {
  width: 210px;
  height: 210px;
  border-right-color: rgba(212, 175, 55, 0.45);
  animation: spin-slow 13s linear infinite reverse;
}

.orb-center {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  background: radial-gradient(
    circle at 35% 35%,
    rgba(212, 175, 55, 0.22),
    rgba(10, 22, 40, 0.9)
  );
  border: 1px solid var(--border);
  box-shadow: 0 0 40px rgba(212, 175, 55, 0.12);
}

@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.exp-badge {
  position: absolute;
  top: 8%;
  right: -10px;
  background: linear-gradient(135deg, var(--gold), var(--gold-dark));
  color: #000;
  border-radius: 16px;
  padding: 1rem 1.4rem;
  text-align: center;
  box-shadow: 0 8px 30px var(--gold-glow);
}

.exp-num {
  display: block;
  font-size: 2.2rem;
  font-weight: 900;
  font-family: "Montserrat", sans-serif;
  line-height: 1;
}

.exp-label {
  font-size: 0.8rem;
  font-weight: 700;
  white-space: nowrap;
}

.team-grid {
  position: absolute;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.team-avatar {
  width: 52px;
  height: 52px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gold);
  transition: all 0.3s;
}

.team-avatar:hover {
  border-color: var(--gold);
  transform: scale(1.12);
  background: rgba(212, 175, 55, 0.1);
}

.about-quote {
  position: absolute;
  bottom: 4%;
  left: -10px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 1rem 1.3rem;
  font-size: 0.85rem;
  color: var(--gray);
  max-width: 230px;
  line-height: 1.7;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}

.quote-mark {
  color: var(--gold);
  font-size: 1.8rem;
  line-height: 0.5;
  display: block;
  margin-bottom: 0.4rem;
}

.about-content {
  flex: 1;
}

.pre-label {
  display: block;
  color: var(--gold);
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 3px;
  margin-bottom: 0.5rem;
}

.about-title {
  font-size: clamp(1.8rem, 3vw, 2.8rem);
  font-weight: 900;
  line-height: 1.3;
  color: var(--white);
  margin-bottom: 0.5rem;
}

.highlight {
  background: linear-gradient(135deg, var(--gold-light), var(--gold));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.gold-divider {
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, var(--gold), transparent);
  margin: 1rem 0 1.5rem;
  border-radius: 2px;
}

.about-text {
  color: var(--gray);
  line-height: 1.9;
  font-size: 1rem;
  margin-bottom: 1.2rem;
}

.values-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin: 1.5rem 0 2rem;
}

.value-item {
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1rem;
  transition: all 0.3s;
}

.value-item:hover {
  border-color: rgba(212, 175, 55, 0.4);
  transform: translateY(-2px);
}

.value-icon {
  width: 36px;
  height: 36px;
  background: rgba(212, 175, 55, 0.08);
  border: 1px solid var(--border);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.value-text {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.value-text strong {
  font-size: 0.9rem;
  color: var(--gold-light);
  font-weight: 700;
}

.value-text span {
  font-size: 0.8rem;
  color: var(--gray);
}

.btn-cta {
  display: inline-block;
  background: linear-gradient(135deg, var(--gold), var(--gold-dark));
  color: #000;
  font-weight: 700;
  padding: 0.9rem 2.5rem;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 6px 24px var(--gold-glow);
  text-decoration: none;
}

.btn-cta:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 36px rgba(212, 175, 55, 0.4);
}

@media (max-width: 900px) {
  .container {
    flex-direction: column-reverse;
    gap: 3rem;
  }
  .about-visual {
    min-height: 300px;
    width: 100%;
  }
  .values-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .about-section {
    padding: 5rem 1.5rem;
  }
  .about-quote {
    left: 0;
    max-width: 190px;
    font-size: 0.78rem;
  }
}
</style>
